import { Injectable } from '@angular/core';
import { ImageApiService } from './image-api.service';
import { Observable, of } from 'rxjs';
import { SasTokenModel } from './sas-token.model';
import heic2any from 'heic2any';
import convert from 'heic-convert/browser';
import * as loadImage from 'blueimp-load-image';
import { MyCampApp } from '../common/enumerations';
import { EventItem } from '../events/event-item.model';
import { tap } from 'rxjs/operators';
import { CachedCollection } from '../common/cached-collection';
import { StockImage } from './stock-image-model';

export class UploadFileResult
{
    fileOrUrl: any;
    width?: number;
    height?: number;

    constructor(fileOrUrl: any, width?: number, height?: number)
    {
        this.fileOrUrl = fileOrUrl;
        this.width = width;
        this.height = height;
    }
}

export class ImageSizeResult
{
    constructor(width: number, height: number)
    {
        this.width = width;
        this.height = height;
    }
    width: number;
    height: number;
}

@Injectable({
    providedIn: 'root'
})
export class ImageManagerService
{
    static MAX_IMAGE_SIZE = 2000;
    static MAX_IMAGE_SIZE_REDUCED = 750;
    static MAX_PDF_FILE_SIZE_MB = 20;
    static MAX_PDF_FILE_SIZE = ImageManagerService.MAX_PDF_FILE_SIZE_MB * 1000000;

    private _stockImages?: StockImage[] = undefined;

    constructor(private imageApiService: ImageApiService)
    {}

    public static resetOrientation(srcBase64: any, srcOrientation: any, callback: any)
    {
        const img = new Image();

        img.onload = function ()
        {
            const width = img.width;
            const height = img.height;
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d')!;

            // set proper canvas dimensions before transform & export
            if (4 < srcOrientation && srcOrientation < 9)
            {
                // noinspection JSSuspiciousNameCombination
                canvas.width = height;
                // noinspection JSSuspiciousNameCombination
                canvas.height = width;
            }
            else
            {
                canvas.width = width;
                canvas.height = height;
            }

            // transform context before drawing image
            switch (srcOrientation)
            {
                case 2:
                    ctx.transform(-1, 0, 0, 1, width, 0);
                    break;
                case 3:
                    ctx.transform(-1, 0, 0, -1, width, height);
                    break;
                case 4:
                    ctx.transform(1, 0, 0, -1, 0, height);
                    break;
                case 5:
                    ctx.transform(0, 1, 1, 0, 0, 0);
                    break;
                case 6:
                    ctx.transform(0, 1, -1, 0, height, 0);
                    break;
                case 7:
                    ctx.transform(0, -1, -1, 0, height, width);
                    break;
                case 8:
                    ctx.transform(0, -1, 1, 0, 0, width);
                    break;
                default:
                    break;
            }

            // draw image
            ctx.drawImage(img, 0, 0);

            // export base64
            callback(canvas.toDataURL());
        };

        img.src = srcBase64;
    }

    private static dataURLToBlob(dataURL: any)
    {
        const BASE64_MARKER = ';base64,';
        if (dataURL.indexOf(BASE64_MARKER) === -1)
        {
            const parts2 = dataURL.split(',');
            const contentType2 = parts2[ 0 ].split(':')[ 1 ];
            const raw2 = parts2[ 1 ];

            return new Blob([ raw2 ], { type: contentType2 });
        }

        const parts = dataURL.split(BASE64_MARKER);
        const contentType = parts[ 0 ].split(':')[ 1 ];
        const raw = window.atob(parts[ 1 ]);
        const rawLength = raw.length;

        const uInt8Array = new Uint8Array(rawLength);

        for (let i = 0; i < rawLength; ++i)
        {
            uInt8Array[ i ] = raw.charCodeAt(i);
        }

        return new Blob([ uInt8Array ], { type: contentType });
    }

    public uploadFile(file?: File, isReduced = false): Observable<UploadFileResult>
    {
        if (file == undefined) return of(new UploadFileResult(undefined, 0, 0));

        const cleanFileName = file.name.toLowerCase().replace(/ /g, '_').replace('.heic', '.jpg');
        return new Observable<UploadFileResult>(observer =>
        {
            this.getSasTokenUrl(cleanFileName).subscribe({
                next: (model: SasTokenModel) =>
                {
                    // if (model == null || model.Token == null) return throwError('Unable to get upload URL.');
                    let uploadResult: UploadFileResult;
                    this.uploadFileWithSasToken(file, model.Token, isReduced).subscribe({
                        next: (result: UploadFileResult) => uploadResult = result,
                        error: error => observer.error(error),
                        complete: () =>
                        {
                            observer.next(new UploadFileResult(model.Token, uploadResult.width, uploadResult.height));
                            observer.complete();
                        }
                    });
                },
                error: (error) => observer.error(error)
            });
        });
    }

    private getSasTokenUrl(filename: string): Observable<SasTokenModel>
    {
        return this.imageApiService.getSasToken(filename);
    }

    private uploadFileWithSasToken(file: File, sasToken: string, isReduced = false): Observable<UploadFileResult>
    {
        return new Observable<UploadFileResult>(observer =>
        {
            this.getResizedFile(file, isReduced)
                .subscribe(uploadFileResult =>
                {
                    this.imageApiService.uploadFile(uploadFileResult.fileOrUrl, sasToken)
                        .subscribe(null,
                            error => observer.error(error),
                            () =>
                            {
                                observer.next(uploadFileResult);
                                observer.complete();
                            }
                        );
                }, error => observer.error(error)
                );
        });
    }

    downloadImageFromUrl(url: string)
    {
        return this.imageApiService.downloadFile(url);
    }

    getImageWithSize(imageId: number, width: number, height: number)
    {
        return this.imageApiService.getImageWithSize(imageId, width, height);
    }

    updateCaption(id: number, caption?: string)
    {
        return this.imageApiService.updateCaption(id, caption);
    }

    checkImageSize(file: File): Observable<ImageSizeResult>
    {
        return new Observable(observer =>
        {
            const img = new Image();

            img.onload = function ()
            {
                const width = img.width;
                const height = img.height;
                observer.next(new ImageSizeResult(width, height));
                observer.complete();
            };

            img.src = window.URL.createObjectURL(file);
        });
    }

    getStockImages(): Observable<StockImage[]>
    {
        if (this._stockImages) return of(this._stockImages);

        return this.imageApiService.getStockImages().pipe(tap(stockImages =>
            {
                this._stockImages = stockImages;
            })
        );
    }

    private getResizedFile(file: File, isReduced = false): Observable<UploadFileResult>
    {
        if (!file.type.match(/image.*/) && file.name.toLowerCase().indexOf('.heic') === -1) return of(new UploadFileResult(file, 0, 0));

        // @ts-ignore
        return new Observable(async observer =>
        {
            if (file.type === 'image/heic' || file.name.toLowerCase().indexOf('.heic') !== -1)
            {
                const arrayBuffer = await file.arrayBuffer();
                const uint8Array = new Uint8Array(arrayBuffer);
                const jpegBlob = await convert({buffer: uint8Array, format: 'JPEG', quality:0.7});
                file = new File([jpegBlob], 'resized.jpg', {
                    type: 'image/jpeg',
                    lastModified: Date.now()
                });
            }
            // Load the image onto a canvas, so we can get a blob to upload (note: this auto-rotates the image)
            loadImage(file, function (canvas, data)
                {
                    if ('toDataURL' in canvas)
                    {
                        const dataUrl = canvas.toDataURL(file.type, 0.7);
                        const resizedImage = ImageManagerService.dataURLToBlob(dataUrl);
                        observer.next(new UploadFileResult(resizedImage, canvas.width, canvas.height));
                        observer.complete();
                        return;
                    }
                },
                {
                    maxWidth: isReduced ? ImageManagerService.MAX_IMAGE_SIZE_REDUCED : ImageManagerService.MAX_IMAGE_SIZE,
                    maxHeight: isReduced ? ImageManagerService.MAX_IMAGE_SIZE_REDUCED : ImageManagerService.MAX_IMAGE_SIZE,
                    canvas: true
                }
            );
        });
    }
}
